import { escapeHtml } from './escapeHtml';
import type { SerializeOptions } from './serializeOptions';
import {
  COMMENT_ATTRIBUTE_NAMES,
  COMMENT_MARKER_ATTRIBUTE_NAMES,
  COMMENT_TAG_NAMES,
  COMMENT_THREAD_ATTRIBUTE_NAMES,
} from '../comment-markup';
import { indexParagraphs } from '../expand/indexParagraphs';
import type {
  CastComment,
  CastCommentThread,
  CastCommentThreads,
  CastInlineContent,
  CastParagraph,
  CastRoot,
  CastText,
} from '../types';

/** A `<comment-start/>` or `<comment-end/>` placed beside a text run. */
export type CommentMarker = {
  /** The comment also covers text past this marker that is not in the output. */
  continues: boolean;
  thread: CastCommentThread;
};

/**
 * Where one serialize call prints each comment. A comment's range is only known
 * across paragraphs, and which part of it shows depends on the `included` flags,
 * so this is worked out over the whole tree before serializing.
 */
export type CommentMarkerPlan = {
  endsAfter: Map<CastText, CommentMarker[]>;
  startsBefore: Map<CastText, CommentMarker[]>;
  threadsAfter: Map<CastParagraph, CastCommentThread[]>;
};

/** One text run a comment covers, in serializer order. */
export type AnchoredRun = {
  included: boolean;
  paragraph: CastParagraph;
  text: CastText;
};

/**
 * Places each comment's markers on the first and last included run it covers, so
 * a start marker always has its end marker in the output, and puts the thread
 * after the paragraph holding the end marker. A comment none of whose runs is
 * included prints nothing. Returns undefined when there is nothing to print.
 */
export function planCommentMarkers(
  root: CastRoot,
  options: SerializeOptions
): CommentMarkerPlan | undefined {
  const threads = root.commentThreads;
  if (!options.emitComments || !threads || threads.size === 0) return undefined;

  const plan: CommentMarkerPlan = {
    endsAfter: new Map(),
    startsBefore: new Map(),
    threadsAfter: new Map(),
  };

  for (const { runs, thread } of collectAnchoredRuns(root, threads).values()) {
    const first = runs.findIndex(run => run.included);
    if (first === -1) continue;
    const last = runs.findLastIndex(run => run.included);

    addToList(plan.startsBefore, runs[first].text, { continues: first > 0, thread });
    // Comments are visited in order of first appearance, so prepending closes them innermost first.
    const endMarker: CommentMarker = { continues: last < runs.length - 1, thread };
    plan.endsAfter.set(runs[last].text, [
      endMarker,
      ...(plan.endsAfter.get(runs[last].text) ?? []),
    ]);
    addToList(plan.threadsAfter, runs[last].paragraph, thread);
  }

  return plan;
}

/**
 * Every run each comment covers, in the order the serializer emits them, keyed by
 * the anchor id and in order of each comment's first appearance. Anchors without
 * a thread are left out.
 */
export function collectAnchoredRuns(
  root: CastRoot,
  threads: CastCommentThreads
): Map<string, { runs: AnchoredRun[]; thread: CastCommentThread }> {
  const byAnchorId = new Map<string, { runs: AnchoredRun[]; thread: CastCommentThread }>();

  for (const { ancestors, paragraph } of indexParagraphs(root).ordered) {
    const included =
      paragraph.included === true && ancestors.every(ancestor => ancestor.included === true);

    for (const text of iterateTextRuns(paragraph.children)) {
      for (const anchorId of text.commentIds ?? []) {
        const thread = threads.get(anchorId);
        if (!thread) continue;

        const anchored = byAnchorId.get(anchorId) ?? { runs: [], thread };
        anchored.runs.push({ included, paragraph, text });
        byAnchorId.set(anchorId, anchored);
      }
    }
  }

  return byAnchorId;
}

export function formatCommentStart(marker: CommentMarker): string {
  return formatMarker({
    continuesAttribute: COMMENT_MARKER_ATTRIBUTE_NAMES.continuesBefore,
    marker,
    tagName: COMMENT_TAG_NAMES.start,
  });
}

export function formatCommentEnd(marker: CommentMarker): string {
  return formatMarker({
    continuesAttribute: COMMENT_MARKER_ATTRIBUTE_NAMES.continuesAfter,
    marker,
    tagName: COMMENT_TAG_NAMES.end,
  });
}

/** One `<comment>` per line, the root comment first and then its replies. */
export function serializeCommentThread(thread: CastCommentThread): string {
  return [
    formatCommentThreadOpenTag(thread),
    ...thread.comments.map(serializeComment),
    `</${COMMENT_TAG_NAMES.thread}>`,
  ].join('\n');
}

export function formatCommentThreadOpenTag(thread: CastCommentThread): string {
  const privateAttr = thread.isPrivate ? ` ${COMMENT_THREAD_ATTRIBUTE_NAMES.isPrivate}="true"` : '';
  return `<${COMMENT_TAG_NAMES.thread} ${COMMENT_THREAD_ATTRIBUTE_NAMES.id}="${escapeHtml(thread.id)}"${privateAttr}>`;
}

function formatMarker(options: {
  continuesAttribute: string;
  marker: CommentMarker;
  tagName: string;
}): string {
  const { continuesAttribute, marker, tagName } = options;
  const continues = marker.continues ? ` ${continuesAttribute}="true"` : '';
  return `<${tagName} ${COMMENT_MARKER_ATTRIBUTE_NAMES.id}="${escapeHtml(marker.thread.id)}"${continues}/>`;
}

function serializeComment(comment: CastComment): string {
  const names = COMMENT_ATTRIBUTE_NAMES;
  const attrs = [`${names.authorName}="${escapeHtml(comment.authorName)}"`];
  if (comment.authorId !== null) attrs.push(`${names.authorId}="${escapeHtml(comment.authorId)}"`);
  attrs.push(
    `${names.party}="${comment.party}"`,
    `${names.createdAt}="${comment.createdAt.toISOString().slice(0, 10)}"`
  );
  const tag = COMMENT_TAG_NAMES.comment;
  return `<${tag} ${attrs.join(' ')}>${escapeHtml(comment.text)}</${tag}>`;
}

function* iterateTextRuns(children: CastInlineContent[]): Generator<CastText> {
  for (const child of children) {
    if (child.type === 'text') yield child;
    else if (child.type === 'link') yield* iterateTextRuns(child.children);
  }
}

function addToList<TKey, TValue>(map: Map<TKey, TValue[]>, key: TKey, value: TValue): void {
  const list = map.get(key);
  if (list) list.push(value);
  else map.set(key, [value]);
}
